/**
 * Knowledge Base Modal Store
 *
 * Owns the modal state + actions for knowledge-base create/edit/delete flows.
 *
 * Relocated from `@/app/store/ui` per FSD compliance pass. The
 * modal state was previously coupled to the global UI store, forcing every
 * knowledgeBases consumer into a cross-app (`@/app/`) import. Moving it here
 * restores feature ownership (no consumers outside the knowledgeBases slice).
 *
 * The public surface mirrors the legacy `useKnowledgeBaseModal` hook so
 * consumers only had to swap their import path.
 */
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import type {
  KnowledgeBaseModalActions,
  KnowledgeBaseModalSetter,
  KnowledgeBaseModalState,
  KnowledgeBaseModalStore,
  KnowledgeBaseModalType,
} from './knowledge-base-modal.store.types';

const initialState: KnowledgeBaseModalState = {
  isKnowledgeBaseModalOpen: false,
  knowledgeBaseModalLoading: false,
  knowledgeBaseModalError: null,
  knowledgeBaseModalType: null,
  knowledgeBaseModalTypeEdit: false,
  knowledgeBaseModalTypeDelete: false,
  knowledgeBaseModalTypeCreate: false,
};

// The three booleans are a denormalisation of `knowledgeBaseModalType`, and
// surfaces read whichever is closer to hand, so a transition has to rewrite all
// of them. Leaving a stale one behind keeps the previous surface mounted over
// the new one.
const openedAs = (type: Exclude<KnowledgeBaseModalType, null>) => ({
  isKnowledgeBaseModalOpen: true,
  knowledgeBaseModalType: type,
  knowledgeBaseModalTypeCreate: type === 'create',
  knowledgeBaseModalTypeEdit: type === 'edit',
  knowledgeBaseModalTypeDelete: type === 'delete',
});

function makeKnowledgeBaseModalActions(set: KnowledgeBaseModalSetter): KnowledgeBaseModalActions {
  return {
    closeKnowledgeBaseModal: () => {
      set((state) => ({
        ...state,
        knowledgeBaseModalType: null,
        knowledgeBaseModalTypeEdit: false,
        knowledgeBaseModalTypeDelete: false,
        knowledgeBaseModalTypeCreate: false,
        isKnowledgeBaseModalOpen: false,
      }));
    },

    setKnowledgeBaseModalLoading: (loading) => {
      set((state) => ({
        ...state,
        knowledgeBaseModalLoading: loading,
      }));
    },

    setKnowledgeBaseModalError: (error) => {
      set((state) => ({
        ...state,
        knowledgeBaseModalError: error,
      }));
    },

    createKnowledgeBaseModal: () => {
      set((state) => ({ ...state, ...openedAs('create') }));
    },

    editKnowledgeBaseModal: () => {
      set((state) => ({ ...state, ...openedAs('edit') }));
    },

    deleteKnowledgeBaseModal: () => {
      set((state) => ({ ...state, ...openedAs('delete') }));
    },

    resetKnowledgeBaseModalState: () => {
      set(() => initialState);
    },
  };
}

const useKnowledgeBaseModalStore = create<KnowledgeBaseModalStore>((set) => ({
  ...initialState,
  ...makeKnowledgeBaseModalActions(set),
}));

// Combined selector — mirrors the legacy `useKnowledgeBaseModal` shape so
// consumers only swap the import path.
export const useKnowledgeBaseModal = () =>
  useKnowledgeBaseModalStore(
    useShallow((state) => ({
      isKnowledgeBaseModalOpen: state.isKnowledgeBaseModalOpen,
      knowledgeBaseModalType: state.knowledgeBaseModalType,
      knowledgeBaseModalTypeEdit: state.knowledgeBaseModalTypeEdit,
      knowledgeBaseModalTypeDelete: state.knowledgeBaseModalTypeDelete,
      knowledgeBaseModalTypeCreate: state.knowledgeBaseModalTypeCreate,
      knowledgeBaseModalLoading: state.knowledgeBaseModalLoading,
      knowledgeBaseModalError: state.knowledgeBaseModalError,
      createKnowledgeBaseModal: state.createKnowledgeBaseModal,
      editKnowledgeBaseModal: state.editKnowledgeBaseModal,
      deleteKnowledgeBaseModal: state.deleteKnowledgeBaseModal,
      closeKnowledgeBaseModal: state.closeKnowledgeBaseModal,
      setKnowledgeBaseModalLoading: state.setKnowledgeBaseModalLoading,
      setKnowledgeBaseModalError: state.setKnowledgeBaseModalError,
      resetKnowledgeBaseModalState: state.resetKnowledgeBaseModalState,
    })),
  );
